import { AppType } from "client/Api/Apps/common";
import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import { AlertManager, useAlerts } from "client/Hooks";
import { deepComparison } from "client/Misc/compareUtils";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	VilansKickConnectionSelectOptionsModel,
	getVilansKickConnections,
	VilansKickConnectionSelectOptionModel
} from "client/Api/Apps/EditApp/appParameters";
import { VilansKickConnectionDialog } from "client/Features/Settings/Connections/VilansKick/VilansKickConnectionDialog";
import { VilansKickConnectionListItemModel } from "client/Api/VilansKickConfiguration/vilansKickConfiguration";
import {
	EditVilansKickAppViewModel,
	SaveVilansKickAppModel,
	VilansKickConnectionIdentifierViewModel,
	saveVilansKickApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditVilansKickAppProps = {
	initialSettings: EditAppSettings<EditVilansKickAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditVilansKickApp = forwardRef(
	(
		props: EditVilansKickAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState<boolean>(false);
		const [formData, setFormData] = useState<SaveVilansKickAppModel>(
			() => convertToSaveModel(initialSettings)
		);
		const [connections, setConnections] = useState<VilansKickConnectionSelectOptionsModel>({
			canCreateNew: false,
			options: []
		});

		const alertManager: AlertManager = useAlerts();
		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveVilansKickAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const connectionOptions: readonly SelectItem<VilansKickConnectionIdentifierViewModel>[] = useMemo(
			(): readonly SelectItem<VilansKickConnectionIdentifierViewModel>[] => {
				return connections.options
					.map(
						o => ({
							value: {
								id: o.id
							},
							text: o.name
						})
					);
			},
			[connections.options]
		);

		const loadVilansKickConnections = useCallback(
			async (signal: AbortSignal): Promise<void> => {
				try {
					const optionsModel: VilansKickConnectionSelectOptionsModel = await getVilansKickConnections(
						signal
					);

					setConnections(optionsModel);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			},
			[alertManager]
		);

		const addConnectionHandler = useCallback(
			(item: VilansKickConnectionListItemModel): void => {
				setConnections((newConnections: VilansKickConnectionSelectOptionsModel) => {
					const existingItemIndex: number = newConnections.options
						.findIndex(i => i.id === item.id);

					const options: VilansKickConnectionSelectOptionModel[] = [
						...newConnections.options
					];

					if (existingItemIndex === -1) {
						options.push(item);
					} else {
						options.splice(existingItemIndex, 1, item);
					}

					return {
						...newConnections,
						options
					};
				});

				formHandler.for("connection")
					.attachToSelect()
					.onChange({ id: item.id });
			},
			[formHandler]
		);

		useEffect(
			() => {
				const cancellation = new AbortController();
				loadVilansKickConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[loadVilansKickConnections]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveVilansKickAppModel>(
			formHandler,
			saveVilansKickApp,
			AppType.VilansKick
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connection", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<>
				<EditAppBase
					formHandler={formHandler}
					hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
					initialAppSettings={app}
					usersAndUserGroupsData={usersAndUserGroupsData}
					initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
				>
					<FormItem label={localize("_Connection")}>
						<InputRow>
							<InputRow.Stretch>
								<Select
									{...formHandler.for("connection").attachToSelect()}
									options={connectionOptions}
									areEqual={deepComparison}
									placeholder={localize("_SelectConnection")}
									menuTitle={localize("_Connection")}
								/>
							</InputRow.Stretch>
							{connections.canCreateNew && (
								<Button
									text={localize("_Add")}
									onClick={() => setIsConnectionDialogOpen(true)}
									kind="secondary"
								/>
							)}
						</InputRow>
						<ValidationError
							{...formHandler.for("connection").attachToValidationError()}
						/>
					</FormItem>
					<AppDestinationWindowSelectFormItem
						attachable={formHandler.for("destinationWindow")}
					/>
				</EditAppBase>
				{isConnectionDialogOpen && (
					<VilansKickConnectionDialog
						onAfterSave={addConnectionHandler}
						onClose={() => setIsConnectionDialogOpen(false)}
					/>
				)}
			</>
		);
	}
);
EditVilansKickApp.displayName = "EditVilansKickApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditVilansKickAppViewModel>
): SaveVilansKickAppModel {
	const {
		destinationWindow,
		connection
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		connection
	};
}

const validation = createValidationConfiguration<SaveVilansKickAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forObject("connection")
	.add(
		isNotNullNorUndefined()
	);
